import { FileJson, X } from 'lucide-react';

import { JsonTreePanel } from '@/components/document-view';
import { SlideDrawer } from '@/components/ui/slide-drawer';

export interface MethodSchemaDrawerPayload {
    methodName: string;
    schema: Record<string, unknown>;
    kind: 'input' | 'output' | 'type_config';
}

interface MethodSchemaDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    payload: MethodSchemaDrawerPayload | null;
}

const KIND_LABEL: Record<MethodSchemaDrawerPayload['kind'], string> = {
    input: 'Input JSON Schema',
    output: 'Output JSON Schema',
    type_config: 'Type config (JSON)',
};

export function MethodSchemaDrawer({ open, onOpenChange, payload }: MethodSchemaDrawerProps) {
    const subtitle = payload ? KIND_LABEL[payload.kind] : '';

    return (
        <SlideDrawer
            isOpen={open && !!payload}
            onClose={() => onOpenChange(false)}
        >
            {payload && (
                <>
                    <div
                        className="relative shrink-0 border-b border-slate-100 bg-slate-50/50 px-6 py-4 sm:px-8"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="method-schema-drawer-title"
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-100">
                                    <FileJson className="h-5 w-5 text-violet-600" aria-hidden />
                                </div>
                                <div className="min-w-0">
                                    <h2
                                        id="method-schema-drawer-title"
                                        className={
                                            payload.kind === 'type_config'
                                                ? 'break-all font-mono text-xl font-semibold text-slate-900'
                                                : 'truncate text-xl font-semibold text-slate-900'
                                        }
                                    >
                                        {payload.methodName}
                                    </h2>
                                    <p className="text-sm text-slate-500">{subtitle}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onOpenChange(false);
                                }}
                                className="shrink-0 rounded-full p-1 text-slate-400 outline-none transition-colors hover:bg-slate-200 hover:text-slate-600 focus:ring-2 focus:ring-slate-900"
                            >
                                <X size={20} />
                                <span className="sr-only">Close</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-white px-6 py-4 sm:px-8">
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-950">
                            <div className="min-h-0 flex-1 overflow-auto">
                                <JsonTreePanel value={payload.schema} />
                            </div>
                        </div>
                    </div>
                </>
            )}
        </SlideDrawer>
    );
}
